// Decide se um item de topo da sidebar (módulo/grupo) deve aparecer.
//
// Regra: além dos gates próprios do item (module/permission/anyOfPermissions),
// um filho com permissão explícita concedida abre o grupo — um cargo só com
// chat.contacts.view precisa ver o grupo Chat pra alcançar Contatos, mesmo sem
// chat.conversations.view. Filhos sem `permission` (default-visíveis, ex.
// Telefonia > Visão ao vivo) e `disabled` (ex. CX "em breve") não contam:
// abririam o grupo pra qualquer cargo.
//
// `module` do item de topo segue hard gate — é licenciamento do ISP
// (TenantModule), não permissão de cargo.

export interface VisibilitySubItem {
  name?: string;
  permission?: string;
  anyPermission?: string;
  module?: string;
  disabled?: boolean;
  gkAdminOnly?: boolean;
}

export interface VisibilityModuleItem<S extends VisibilitySubItem = VisibilitySubItem> {
  alwaysVisible?: boolean;
  module?: string;
  permission?: string;
  anyOfPermissions?: string[];
  children?: S[];
  groups?: { items: S[] }[];
}

export interface VisibilityDeps<S extends VisibilitySubItem = VisibilitySubItem> {
  hasPermission: (code: string) => boolean;
  hasModule: (name: string) => boolean;
  /** Mesmo predicado usado pra renderizar os filhos — mantém coerência: filho que abre o grupo é filho que renderiza. */
  isSubItemVisible: (sub: S) => boolean;
}

export function isModuleItemVisible<S extends VisibilitySubItem>(
  item: VisibilityModuleItem<S>,
  deps: VisibilityDeps<S>,
): boolean {
  if (item.alwaysVisible) return true;
  if (item.module && !deps.hasModule(item.module)) return false;

  // Itens soltos E grupos: um módulo pode ter os dois (CX tem Clientes, Churn…
  // soltos e o grupo Jornada do cliente). Ler só um lado fazia um cargo com
  // permissão apenas num item solto perder o módulo inteiro.
  const subs = [...(item.children ?? []), ...(item.groups?.flatMap((g) => g.items) ?? [])];
  const hasGrantedChild = subs?.some(
    (c) => !c.disabled && !!c.permission && deps.isSubItemVisible(c),
  );
  if (hasGrantedChild) return true;

  if (item.anyOfPermissions) {
    return item.anyOfPermissions.some((p) => deps.hasPermission(p));
  }
  return !item.permission || deps.hasPermission(item.permission);
}
